// Estados de cuenta de comisiones: lo que Fiado depositó en cada periodo de cobro, para cobrárselo
// a la cadena. Con `retailerId`, los de esa cadena; con `null`, los de la cadena del token.
import { useState } from 'react';
import type { RetailCommission } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { Button } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { MasterDetail, MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { useStatements } from '../../api/commissionQueries';
import { BILLING_PERIOD_LABEL } from '../../lib/ruleText';
import { sortStatements, statementPeriodText } from '../../lib/statementRows';
import { StatementDetail } from './StatementDetail';

type Statement = RetailCommission.CommissionChainStatement;

interface StatementsExplorerProps {
    /** `null`: la cadena del token (Admin VL). */
    retailerId: string | null;
    sellerName: (sellerId: string) => string;
}

export function StatementsExplorer({ retailerId, sellerName }: StatementsExplorerProps) {
    const statements = useStatements(retailerId);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const rows = sortStatements(statements.data?.statements ?? []);
    // Sin elección se abre el más reciente.
    const selected = rows.find((s) => s.statementId === selectedId) ?? rows.at(0) ?? null;

    const columns: DataTableColumn<Statement>[] = [
        { key: 'period', label: 'Periodo', cell: (s) => statementPeriodText(s) },
        {
            key: 'kind',
            label: 'Cobro',
            width: '80px',
            cell: (s) => BILLING_PERIOD_LABEL[s.period],
        },
        {
            key: 'sellers',
            label: 'Vendedores',
            width: '84px',
            numeric: true,
            cell: (s) => s.sellersCount,
        },
        {
            key: 'payments',
            label: 'Depósitos',
            width: '80px',
            numeric: true,
            cell: (s) => s.paymentsCount,
        },
        {
            key: 'amount',
            label: 'Depositado',
            width: '110px',
            numeric: true,
            cellClassName: () => 'font-semibold',
            cell: (s) => formatMoneyCents(s.accreditedCents),
        },
    ];

    const list = (
        <div className="flex min-h-0 flex-1 flex-col">
            <TableToolbar>
                <div className="flex-1" />
                <ToolbarMetric label="Estados" value={statements.error ? '—' : rows.length} />
            </TableToolbar>
            {statements.error ? (
                <UnavailableState
                    title="No se pudieron cargar los estados de cuenta"
                    body={userMessageOf(statements.error)}
                    action={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                void statements.refetch();
                            }}
                        >
                            Reintentar
                        </Button>
                    }
                />
            ) : (
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(s) => s.statementId}
                    caption="Estados de cuenta"
                    isRowSelected={(s) => s.statementId === selected?.statementId}
                    onRowClick={(s) => {
                        setSelectedId(s.statementId);
                    }}
                    isLoading={statements.isLoading}
                    empty="Todavía no hay estados de cuenta: se generan al cerrar cada periodo de cobro."
                    density="compact"
                />
            )}
        </div>
    );

    const detail = selected ? (
        <StatementDetail
            key={selected.statementId}
            statement={selected}
            retailerId={retailerId}
            sellerName={sellerName}
        />
    ) : (
        <MasterDetailEmpty
            title="Sin estado de cuenta"
            sub="Elige uno de la lista para ver sus depósitos."
        />
    );

    return <MasterDetail list={list} detail={detail} />;
}
